/* eslint-disable no-underscore-dangle */
import {
  DowncastWriter,
  Element,
  Plugin,
  toWidget,
  viewToModelPositionOutsideModelElement,
  Widget,
} from 'ckeditor5'

import { CommandName } from '../../types'
import PlaceholderCommand from './placeholderCommand'

import './placeholderTheme.css'

const placeholderClass = 'PLACEHOLDER'
const initialSpecialChars = '<<'
const endSpecialChars = '>>'

// Helper method for both downcast converters.
function createPlaceholderView(
  modelElement: Element,
  viewWriter: DowncastWriter,
  isDataDowncast: boolean,
) {
  const name = modelElement.getAttribute('name')
  const nameTranslation = modelElement.getAttribute('nameTranslation')
  const placeholderView = viewWriter.createContainerElement('span', {
    class: placeholderClass,
  })

  // Insert placeholder name (as a text) - for data downcast we send the raw name
  // while for editing we use nameTranslation
  const innerText = viewWriter.createText(
    `${initialSpecialChars}${
      isDataDowncast ? name : nameTranslation
    }${endSpecialChars}`,
  )

  viewWriter.insert(viewWriter.createPositionAt(placeholderView, 0), innerText)

  return placeholderView
}

export default class PlaceholderEditing extends Plugin {
  static get requires() {
    return [Widget]
  }

  init() {
    this._defineSchema()
    this._defineConverters()

    this.editor.commands.add(
      CommandName.INSERT_PLACEHOLDER,
      new PlaceholderCommand(this.editor),
    )

    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, viewElement =>
        viewElement.hasClass(placeholderClass),
      ),
    )
  }

  _defineSchema() {
    const { schema } = this.editor.model

    schema.register('placeholder', {
      allowWhere: '$text',
      isInline: true,
      isObject: true,
      allowAttributesOf: '$text',
      allowAttributes: ['name', 'nameTranslation'],
    })
  }

  _defineConverters() {
    const { config, conversion } = this.editor

    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: [placeholderClass],
      },
      model: (viewElement, { writer: modelWriter }) => {
        // Extract the "name" from "<<PlaceholderName>>".
        // Using slice we will always keep the format this way:
        // view: <span class=\"PLACEHOLDER\">&lt;&lt;Some Placeholder&gt;&gt;</span>
        // model: <placeholder name="Some Placeholder"></placeholder>
        const name = (viewElement.getChild(0) as any).data.slice(
          initialSpecialChars.length,
          -endSpecialChars.length,
        )

        const Placeholders = config.get('placeholderConfig.types') as any[]
        const { nameTranslation } =
          Placeholders.map((group: any) => group.items)
            .flat()
            .find((item: any) => item.name === name) || {}

        return modelWriter.createElement('placeholder', {
          name,
          nameTranslation,
        })
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'placeholder',
      view: (modelElement, { writer: viewWriter }) => {
        const widgetElement = createPlaceholderView(
          modelElement,
          viewWriter,
          false,
        )

        return toWidget(widgetElement, viewWriter)
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'placeholder',
      view: (modelElement, { writer: viewWriter }) =>
        createPlaceholderView(modelElement, viewWriter, true),
    })
  }
}
